import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '../api.js'
import { AuthCard, authCardStyles as styles } from '../auth-shell/AuthCard.js'
import {
  useLoginCodeStatus,
  useRequestLoginCode,
  useRequestTenantOtp,
  useVerifyTenantOtp,
} from '../tenant-auth/queries.js'
import { Button, Input, QrCode } from '../ui/index.js'
import pageStyles from './TenantLoginPage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-040`'s original phone→WhatsApp-OTP flow, plus `LGH-050`/`PLT-106`'s
 * reverse flow, **offered alongside** rather than replacing it — `1C`'s own
 * REQ is explicit both mechanisms coexist, and the forward path's OTP still
 * needs a Meta-approved template that doesn't exist in production yet
 * (`WAB-001`'s own deferred item), while reverse needs none. Reverse is the
 * DEFAULT for exactly that reason: it is the one that actually works today
 * without design-partner-blocking prerequisites. A quiet link swaps to the
 * other, so neither path is removed.
 *
 * `LGH-040`'s own doc comment below is kept verbatim for the forward half —
 * still true, still that flow's own history.
 * ───────────────────────────────────────────────────────────────────────────
 * `LGH-040`. The customer console's own login — phone in, a code out,
 * `customer-app`'s `pruvato_tenant_session` cookie on success (`DR-033`:
 * called directly, cross-origin same-site, no proxy).
 *
 * No task filed this screen by name — every customer-console task
 * (`LGH-039`–`042`) assumes "a real login" without one owning the login
 * *screen* itself. Built here as the minimum needed to make `LGH-040`
 * demonstrable; worth the lead's attention as a gap in how the four tasks
 * were split, not a scope decision made unilaterally here.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `DES-001`, presentation only — the two mutations, their order, and what they
 * send are untouched.
 *
 * - The code arrives **over WhatsApp** (`FND-047`, `SPEC-ONB-001`) and this
 *   screen never said so. "Enter the code sent to +9715…" sends a UAE user to
 *   their SMS inbox for a message that will never arrive there.
 * - Both errors rendered in body ink, so a refusal was typographically
 *   identical to the instruction above it. Every other customer screen already
 *   uses `--stop` for exactly this.
 * - `type="text"` with no `inputMode`/`autoComplete` meant an alphabetic
 *   keypad for a phone number and no one-time-code autofill for the code — on
 *   the form factor this flow is most often opened in, since the link that
 *   leads here arrives in WhatsApp.
 * ═══════════════════════════════════════════════════════════════════════════
 */

function ReverseLoginFlow({ onSignedIn }: { onSignedIn: () => void }) {
  const [phone, setPhone] = useState('')
  const [session, setSession] = useState<{ code: string; deepLink: string } | null>(null)
  const requestCode = useRequestLoginCode()
  const status = useLoginCodeStatus(session?.code ?? null)
  const confirmed = status.data?.status === 'confirmed'

  useEffect(() => {
    if (confirmed) onSignedIn()
  }, [confirmed, onSignedIn])

  if (session === null) {
    return (
      <form
        className={styles['form']}
        onSubmit={(event) => {
          event.preventDefault()
          requestCode.mutate(phone.trim(), {
            onSuccess: (response) => {
              setSession({ code: response.code, deepLink: response.deepLink })
            },
          })
        }}
      >
        <Input
          label="Mobile number"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          autoFocus
          placeholder="+971 50 123 4567"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value)
          }}
        />
        <div className={styles['actions']}>
          <Button
            type="submit"
            variant="primary"
            disabled={phone.trim() === ''}
            loading={requestCode.isPending}
          >
            {requestCode.isPending ? 'Generating…' : 'Get a sign-in code'}
          </Button>
        </div>
        {requestCode.isError && (
          <p role="alert" className={styles['error']}>
            {requestCode.error instanceof ApiError
              ? requestCode.error.message
              : 'A code could not be generated.'}
          </p>
        )}
      </form>
    )
  }

  const expired = status.data?.status === 'expired'

  return (
    <div className={pageStyles['codePanel']}>
      <div className={pageStyles['qrWrap']}>
        <QrCode value={session.deepLink} alt="Scan with WhatsApp to sign in" />
      </div>
      <p className={pageStyles['code']}>{session.code}</p>
      <p>
        Tap on your phone, or scan the code above:{' '}
        <a
          className={pageStyles['deepLink']}
          href={session.deepLink}
          target="_blank"
          rel="noreferrer"
        >
          Open WhatsApp
        </a>
      </p>
      {!expired && (
        <p className={pageStyles['status']} role="status">
          Waiting for your message…
        </p>
      )}
      {expired && (
        <>
          <p role="alert" className={styles['error']}>
            This code expired before it was confirmed.
          </p>
          <button
            type="button"
            className={styles['quietAction']}
            onClick={() => {
              setSession(null)
            }}
          >
            Request a new code
          </button>
        </>
      )}
      {!expired && (
        <button
          type="button"
          className={styles['quietAction']}
          onClick={() => {
            setSession(null)
          }}
        >
          Use a different number
        </button>
      )}
    </div>
  )
}

function ForwardOtpFlow({ onSignedIn }: { onSignedIn: () => void }) {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const requestOtp = useRequestTenantOtp()
  const verifyOtp = useVerifyTenantOtp()

  if (!codeSent) {
    return (
      <form
        className={styles['form']}
        onSubmit={(event) => {
          event.preventDefault()
          requestOtp.mutate(phone.trim(), {
            onSuccess: () => {
              setCodeSent(true)
            },
          })
        }}
      >
        <Input
          label="Mobile number"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          autoFocus
          placeholder="+971 50 123 4567"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value)
          }}
        />
        <div className={styles['actions']}>
          <Button
            type="submit"
            variant="primary"
            disabled={phone.trim() === ''}
            loading={requestOtp.isPending}
          >
            {requestOtp.isPending ? 'Sending…' : 'Send code'}
          </Button>
        </div>
        {requestOtp.isError && (
          <p role="alert" className={styles['error']}>
            {requestOtp.error instanceof ApiError
              ? requestOtp.error.message
              : 'That code could not be sent.'}
          </p>
        )}
      </form>
    )
  }

  return (
    <form
      className={styles['form']}
      onSubmit={(event) => {
        event.preventDefault()
        verifyOtp.mutate({ phone: phone.trim(), code: code.trim() }, { onSuccess: onSignedIn })
      }}
    >
      <p className={styles['lede']}>
        We sent a six-digit code on WhatsApp to <strong>{phone}</strong>.
      </p>
      <Input
        label="Six-digit code"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        placeholder="000000"
        value={code}
        onChange={(event) => {
          setCode(event.target.value)
        }}
        {...(verifyOtp.isError
          ? {
              error:
                verifyOtp.error instanceof ApiError
                  ? verifyOtp.error.message
                  : 'That code was not accepted.',
            }
          : {})}
      />
      <div className={styles['actions']}>
        <Button
          type="submit"
          variant="primary"
          disabled={code.trim() === ''}
          loading={verifyOtp.isPending}
        >
          {verifyOtp.isPending ? 'Verifying…' : 'Sign in'}
        </Button>
        <button
          type="button"
          className={styles['quietAction']}
          onClick={() => {
            setCodeSent(false)
            setCode('')
          }}
        >
          Use a different number
        </button>
      </div>
    </form>
  )
}

export function TenantLoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [mode, setMode] = useState<'reverse' | 'forward'>('reverse')

  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/customer/team'

  const onSignedIn = useCallback(() => {
    navigate(from, { replace: true })
  }, [navigate, from])

  return (
    <AuthCard
      documentTitle="Sign in · Pruvato"
      title="Sign in"
      lede={
        mode === 'reverse'
          ? 'Enter the mobile number your account was set up with. Send a short code to Pruvato on WhatsApp yourself to sign in.'
          : "Enter the mobile number your account was set up with. We'll send a six-digit code to it on WhatsApp."
      }
      footnote={
        <button
          type="button"
          className={styles['quietAction']}
          onClick={() => {
            setMode(mode === 'reverse' ? 'forward' : 'reverse')
          }}
        >
          {mode === 'reverse'
            ? 'Have a code already? Enter it instead →'
            : 'Scan or tap to sign in over WhatsApp instead →'}
        </button>
      }
    >
      {mode === 'reverse' ? (
        <ReverseLoginFlow onSignedIn={onSignedIn} />
      ) : (
        <ForwardOtpFlow onSignedIn={onSignedIn} />
      )}
    </AuthCard>
  )
}
